import { FC, ReactNode, SyntheticEvent } from 'react';

import {
    Banner,
    BodyText,
    Button,
    ButtonGroup,
    Headline,
    Modal,
    Stack,
} from '@servicetitan/design-system';

interface CustomModalProps {
    dialog?: boolean;
    size?: (typeof Modal.Sizes)[keyof typeof Modal.Sizes];
    title: string;
    subtitle?: string;
    primaryActionName: string;
    primaryActionNegative?: boolean;
    primaryActionLoading?: boolean;
    onPrimaryActionClick: (e: SyntheticEvent<HTMLElement>) => void;
    secondaryActionName?: string;
    onSecondaryActionClick?: (e: SyntheticEvent<HTMLElement>) => void;
    errorMessage?: string;
    children?: ReactNode;
}

export const CustomModal: FC<CustomModalProps> = ({
    dialog,
    size,
    title,
    subtitle,
    primaryActionName,
    primaryActionNegative,
    primaryActionLoading,
    onPrimaryActionClick,
    secondaryActionName,
    onSecondaryActionClick,
    errorMessage,
    children,
}) => {
    return (
        <Modal.Window size={size} open>
            <div className={'Modal__header' + (dialog ? ' Dialog__header' : '')}>
                <Stack direction="column" className="w-100">
                    <Stack direction="row">
                        <Headline className="Modal__header-title">{title}</Headline>
                        <Button
                            fill="subtle"
                            className="Modal__header-close"
                            iconName="clear"
                            size="xsmall"
                            disabled={primaryActionLoading}
                            onClick={onSecondaryActionClick ?? onPrimaryActionClick}
                            aria-label="close modal"
                        />
                    </Stack>
                    {subtitle && (
                        <BodyText className={dialog ? 'm-y-half' : 'm-t-half'} size="small" subdued>
                            {subtitle}
                        </BodyText>
                    )}
                </Stack>
            </div>
            <Modal.Content className={dialog ? 'Dialog__content' : undefined}>
                <Stack direction="column" spacing={2}>
                    {errorMessage && <Banner status="critical" icon title={errorMessage} />}
                    {children}
                </Stack>
            </Modal.Content>
            <Modal.Footer className={dialog ? 'Dialog__footer' : undefined}>
                <ButtonGroup>
                    {!!secondaryActionName && (
                        <Button
                            small={dialog}
                            disabled={primaryActionLoading}
                            onClick={onSecondaryActionClick}
                        >
                            {secondaryActionName}
                        </Button>
                    )}
                    <Button
                        small={dialog}
                        primary
                        negative={primaryActionNegative}
                        loading={primaryActionLoading}
                        onClick={onPrimaryActionClick}
                    >
                        {primaryActionName}
                    </Button>
                </ButtonGroup>
            </Modal.Footer>
        </Modal.Window>
    );
};
CustomModal.displayName = 'CustomModal';
